import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { newItemId } from "../../../shared/references/itemIds";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { ItemKind } from "../../../api/content/reviewDecoders";
import { LibraryPicker } from "../../search";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import { useHerePath, useThreadPreparation } from "../../threads/prepare";
import AddToChangeSet from "../draft/AddToChangeSet";
import { listedItem, type AuthoringOptions } from "../draft/authoringOptions";
import DefinitionPanel from "../draft/DefinitionPanel";
import FieldProblems from "../draft/FieldProblems";
import { itemEditAt } from "../draft/itemEdits";
import { useDefinitionCheck } from "../draft/useDefinitionCheck";
import { useUnsavedDraft } from "../draft/useUnsavedDraft";
import SimilarItemsPanel from "../similar/SimilarItemsPanel";
import { CONTROL_ROW_CLASS } from "../shared/authoringStyles";
import MarkdownField from "../../../ui/forms/MarkdownField";
import Section from "../../../ui/forms/Section";
import { bundledFileRow, NEW_SKILL, skillContent, skillDraftProblems, type BundledFileRow, type SkillDraft } from "./skillDraft";
import Field from "../../../ui/forms/Field";
import EmptyState from "../../../ui/feedback/EmptyState";

/**
 * The form for a new skill (processes.md P-4, Johannes 2026-10-19: "not only create
 * workflows but also skills"): name, description, the SKILL.md body, and files bundled
 * from reviewed tools and artifacts. It is saved into a change set like a step or an
 * artifact; curators of skills review its text before agents can use it.
 */
export default function SkillForm({ projectId, options }: { projectId: string; options: AuthoringOptions }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SkillDraft>(NEW_SKILL);
  const [itemId] = useState(newItemId);

  const preparation = useThreadPreparation();
  const here = useHerePath();
  const content = useMemo(() => skillContent(draft), [draft]);
  const { connectSaver, markSaved } = useUnsavedDraft(draft);
  const check = useDefinitionCheck(projectId, content, null);
  const problems = skillDraftProblems(draft);
  const commits = useCallback((): readonly CommitBuilder[] => [async (head) => [await itemEditAt(projectId, head, { itemId, base: null }, content)]], [projectId, itemId, content]);
  const setFiles = (files: readonly BundledFileRow[]) => { setDraft({ ...draft, files }); };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="space-y-4">
        <div className="flex justify-end">
          <AppButton variant="ghost" size="sm" onClick={() => { preparation.prepare({ kind: "author", projectId, returnTo: here, itemKind: "skill", draft: content, name: draft.name }); }}>{t("authoring.skill.withAgent")}</AppButton>
        </div>
        <Section title={t("authoring.skill.about")}>
          <Field label={t("authoring.skill.name")} hint={t("authoring.skill.nameHelp")}>
            {(control) => (
              <input {...control} className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`} value={draft.name} placeholder="report-navigation" onChange={(event) => { setDraft({ ...draft, name: event.target.value }); }} />
            )}
          </Field>
          <FieldProblems check={check} path="name" />
          <Field label={t("authoring.skill.description")} hint={t("authoring.skill.descriptionHelp")}>
            {(control) => (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={draft.description} onChange={(event) => { setDraft({ ...draft, description: event.target.value }); }} />
            )}
          </Field>
          <FieldProblems check={check} path="description" />
        </Section>
        <Section title={t("authoring.skill.body")}>
          <p className="app-help-text text-xs">{t("authoring.skill.bodyHelp")}</p>
          <MarkdownField label={t("authoring.skill.body")} labelHidden value={draft.body} rows={18} onChange={(body) => { setDraft({ ...draft, body }); }} />
          <FieldProblems check={check} path="body" />
        </Section>
        <Section title={t("authoring.skill.files")}>
          <BundledFiles rows={draft.files} options={options} onChange={setFiles} />
          <FieldProblems check={check} path="files" />
        </Section>
      </div>
      <div className="space-y-4">
        <SimilarItemsPanel projectId={projectId} kind="skill" name={draft.name} description={draft.description} />
        <DefinitionPanel content={content} base={null} check={check} />
        <AddToChangeSet
          ref={connectSaver}
          projectId={projectId}
          defaultTitle={t("authoring.skill.addTitle", { name: draft.name || t("authoring.skill.unnamed") })}
          commits={commits}
          blockedBy={problems[0] !== undefined ? t(`authoring.skill.problems.${problems[0]}`) : check.status !== "checked" || check.problems.length > 0 ? t("authoring.skill.blocked") : null}
          onSaved={markSaved}
          notice={<p className="app-help-text text-xs">{t("authoring.skill.newNotice")}</p>}
        />
      </div>
    </div>
  );
}

const BUNDLED_KINDS: readonly ItemKind[] = ["tool", "artifact"];

/** Files the skill bundles: each a reviewed tool version or an artifact, at a path the agent reads. */
function BundledFiles({ rows, options, onChange }: { rows: readonly BundledFileRow[]; options: AuthoringOptions; onChange: (rows: readonly BundledFileRow[]) => void }) {
  const { t } = useTranslation();
  const bundled = new Set(rows.map((row) => row.item));
  return (
    <div className="space-y-2">
      <p className="app-help-text text-xs">{t("authoring.skill.filesHelp")}</p>
      {rows.length === 0 ? <EmptyState size="small">{t("authoring.skill.noFiles")}</EmptyState> : null}
      <ul className="space-y-1">
        {rows.map((row, index) => (
          <li key={`${row.source}:${row.item}`} className={CONTROL_ROW_CLASS}>
            <input
              className={`${FORM_COMPACT_CONTROL_CLASS} w-72 font-mono`}
              aria-label={t("authoring.skill.filePath", { position: index + 1 })}
              value={row.path}
              onChange={(event) => { onChange(rows.map((candidate, at) => (at === index ? { ...candidate, path: event.target.value } : candidate))); }}
            />
            <span>{t(`authoring.skill.source.${row.source}`)}: {listedItem(options, row.item)?.name ?? row.item}</span>
            <AppButton variant="ghost" size="xs" aria-label={t("authoring.skill.removeFile", { position: index + 1 })} onClick={() => { onChange(rows.filter((_, at) => at !== index)); }}>
              {t("authoring.skill.remove")}
            </AppButton>
          </li>
        ))}
      </ul>
      <LibraryPicker
        projectId={options.projectId}
        kinds={BUNDLED_KINDS}
        buttonLabel={t("authoring.skill.addFile")}
        searchLabel={t("authoring.skill.chooseItem")}
        onPick={(found) => {
          const item = listedItem(options, found.reference);
          if (item !== undefined && !bundled.has(item.reference) && !bundled.has(item.itemId)) onChange([...rows, bundledFileRow(item)]);
        }}
      />
    </div>
  );
}
